<template>
  <div class="entry-wrapper" :class="wrapperClasses">
    
    <div v-if="isSingleView" class="full-entry-layout">
      
      <div class="nav-header">
        <router-link to="/" class="back-link">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          <span>Back to Feed</span>
        </router-link>
      </div>

      <article class="entry-content">
        <header class="article-header">
          <div class="meta-top">
            <span class="date">{{ formattedDate }}</span>
          </div>
          <h1 class="headline">{{ title }}</h1>
          <div class="author-row">
            <span class="author-by">by</span>
            <span class="author-name">{{ authorName }}</span>
          </div>
        </header>

        <div class="media-container full-media">
          <div v-if="embedUrl" class="video-wrapper">
            <iframe 
              :src="embedUrl" 
              frameborder="0" 
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
              allowfullscreen>
            </iframe>
          </div>
          <div v-else class="image-wrapper">
             <img :src="heroImage" alt="Article Hero" />
          </div>
        </div>

        <div class="action-bar">
          <a v-if="sourceUrl" :href="sourceUrl" target="_blank" class="source-btn">
            Watch on YouTube
            <svg class="icon-sm" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
              <polyline points="15 3 21 3 21 9"></polyline>
              <line x1="10" y1="14" x2="21" y2="3"></line>
            </svg>
          </a>
        </div>

        <div class="article-body">
          <div class="description-content" :class="{ 'description-collapsed': !descriptionExpanded }">
            <p class="description">{{ description }}</p>
          </div>
          <button 
            v-if="isDescriptionLong" 
            class="read-more-toggle" 
            @click="descriptionExpanded = !descriptionExpanded"
          >
            {{ descriptionExpanded ? 'Show Less' : 'Read More' }}
          </button>
        </div>

        <div class="divider"></div>

        <section class="comments-section">
          <div class="comments-header-row">
            <h3 class="comments-title">
              Comments <span class="count-badge">{{ commentCount }}</span>
            </h3>
            
            <button 
              v-if="threadedComments.length" 
              class="thread-toggle-all" 
              @click="toggleAllThreads"
            >
              {{ areThreadsExpanded ? 'Collapse All' : 'Expand All' }}
            </button>
          </div>

          <div class="comments-list" v-if="threadedComments.length">
            <comment 
              v-for="comment in threadedComments" 
              :key="comment.id || comment.externalId" 
              :comment="comment"
              :allow-truncation="false"
              :show-thread="true" 
              :force-expand="areThreadsExpanded"
            />
          </div>
          <div v-else class="no-comments">No comments yet.</div>
        </section>
      </article>
    </div>

    <div v-else class="tile-card">
      <a :href="entryUrl">
        <div class="tile-media">
          <img :src="heroImage" @error="handleImageError" alt="Thumb" class="tile-img" />
          <div class="tile-overlay"></div>
        </div>
      </a>

      <div class="tile-content">
        <div class="tile-meta">
          <a :href="authorLink" class="tile-source">{{ authorName }}</a>
          <span class="tile-dot">&middot;</span>
          <span class="tile-date">{{ formattedDate }}</span>
        </div>
        <a :href="entryUrl">
          <h3 class="tile-title" :title="title">{{ title }}</h3>
        </a>
        
        <p class="tile-desc">{{ description }}</p>

        <div class="tile-actions">
           <a class="read-story-btn" :href="entryUrl">View Post</a>
           
           <a v-if="sourceUrl" :href="sourceUrl" target="_blank" @click.stop class="tile-source-link" title="Watch on YouTube">
             <span class="source-text">YouTube</span>
             <svg class="icon-sm" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
               <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
               <polyline points="15 3 21 3 21 9"></polyline>
               <line x1="10" y1="14" x2="21" y2="3"></line>
             </svg>
           </a>
        </div>

        <div class="tile-comments" v-if="maxComments > 0 && comments.length > 0">
          <div class="tile-divider"></div>
          <div class="comments-preview-list">
            <comment
              v-for="(comment) in visibleComments"
              :key="comment.id"
              :comment="comment"
              :allow-truncation="true"
              :show-thread="false"
              class="tile-comment-item"
            />
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import Comment from "@/components/site/Comment.vue";

export default {
  name: "Entry",
  components: {
    Comment
  },
  props: {
    entry: { type: Object, required: true },
    tileType: { type: String, default: 'single' },
    maxComments: { type: Number, default: 0 },
    showBackLink: { type: Boolean, default: true }
  },
  data() {
    return {
      imageError: false,
      descriptionExpanded: false,
      areThreadsExpanded: true 
    };
  },
  computed: {
    isSingleView() { return this.tileType === 'single'; },
    wrapperClasses() {
      return {
        'mode-full': this.isSingleView,
        'mode-tile': !this.isSingleView,
        [`style-${this.tileType}`]: !this.isSingleView
      };
    },
    entryData() { return this.entry.entry || this.entry || {}; },
    content() { return this.entryData.content || {}; },
    title() { return this.content.title || 'Untitled'; },
    description() { return this.content.description || ''; },
    isDescriptionLong() {
      return this.description && this.description.length > 300;
    },
    authorName() {
      const d = this.entryData;
      return (d.content && d.content.channelTitle) || 'Unknown Source';
    },
    authorLink() {
      const d = this.entryData;
      return `https://www.youtube.com/channel/${d.content.channelId}`;
    },
    formattedDate() {
      const dateVal = this.content.publishedAt || Date.now();
      const date = new Date(dateVal);
      return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    },
    heroImage() {
      if (this.imageError) return 'https://via.placeholder.com/600x400?text=No+Image';
      const thumbs = this.entryData.thumbnails;
      if (thumbs) {
        if (thumbs.maxres && thumbs.maxres.url) return thumbs.maxres.url;
        if (thumbs.high && thumbs.high.url) return thumbs.high.url;
        if (thumbs.standard && thumbs.standard.url) return thumbs.standard.url;
      }
      return 'https://www.commentoid.com/static/commentoid-og-image.png';
    },
    entryUrl() {
       if (this.entry.entry && this.entry.entry.urlId) {
          return "/e/" + this.entry.entry.urlId;
       }
       return "#";
    },
    sourceUrl() {
      if (this.entry.entryId) {
         return "https://www.youtube.com/watch?v=" + this.entry.entryId;
      }
      return this.content.url || this.content.originalLink;
    },
    embedUrl() {
      const videoId = this.entry.entryId || this.content.videoId || this.extractVideoId(this.content.url);
      if (videoId) return 'https://www.youtube.com/embed/' + videoId;
      return null;
    },
    comments() { return this.entry.comments || []; },
    
    visibleComments() {
      if (this.maxComments <= 0) return [];
      return this.comments.slice(0, this.maxComments);
    },
    
    threadedComments() {
      if (!this.comments || !this.comments.length) return [];

      // 1. Safe Deep Clone (prevents mutation issues)
      const raw = JSON.parse(JSON.stringify(this.comments));
      const map = {};
      const roots = [];

      // 2. Initialize Map
      for (var i = 0; i < raw.length; i++) {
        var c = raw[i];
        // Handle flattened vs wrapped structure safely
        var content = c.content || c;
        var id = c.externalId || content.externalId || c.id;

        if (id) {
          c.replies = []; 
          map[id] = c;
        }
      }

      // 3. Build Tree
      for (var i = 0; i < raw.length; i++) {
        var c = raw[i];
        var content = c.content || c;
        var id = c.externalId || content.externalId || c.id;
        
        var parentId = c.parentId || content.parentId;
        
        // Fallback for dot notation (e.g. "ParentID.ChildID")
        if (!parentId && id && id.indexOf('.') > -1) {
          var parts = id.split('.');
          if (parts.length > 1) {
            parentId = parts[0];
          }
        }

        if (parentId && map[parentId]) {
          map[parentId].replies.push(c);
        } else {
          roots.push(c);
        }
      }

      // 4. Sort function (Oldest First)
      var sortByDate = function(a, b) {
         var dA = a.content ? a.content.publishedAt : a.publishedAt;
         var dB = b.content ? b.content.publishedAt : b.publishedAt;
         var dateA = new Date(dA || 0);
         var dateB = new Date(dB || 0);
         return dateA - dateB; 
      };

      // Sort replies within map
      var keys = Object.keys(map);
      for (var k = 0; k < keys.length; k++) {
        var item = map[keys[k]];
        if (item.replies && item.replies.length > 0) {
           item.replies.sort(sortByDate);
        }
      }

      return roots;
    },
    commentCount() { return this.comments.length; }
  },
  methods: {
    handleImageError() { this.imageError = true; },
    extractVideoId(url) {
      if (!url) return null;
      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
      const match = url.match(regExp);
      return (match && match[2].length === 11) ? match[2] : null;
    },
    goToEntry() {
      if (this.isSingleView) return;
      if (this.entryUrl && this.entryUrl !== "#") {
        window.location.href = this.entryUrl;
      }
    },
    toggleAllThreads() {
      this.areThreadsExpanded = !this.areThreadsExpanded;
    }
  }
};
</script>

<style scoped>
/* Include all previous CSS ... */
/* ... */

/* New Styles for Header Row */
.comments-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.thread-toggle-all {
  background: none;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 4px 10px;
  font-size: 0.75rem;
  color: #555;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;
}
.thread-toggle-all:hover {
  background: #f5f5f5;
  color: #111;
}

/* =========================================
   COMMON
   ========================================= */
.entry-wrapper {
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, sans-serif;
  color: #111;
  box-sizing: border-box;
  width: 0;
  min-width: 100%;
}
.icon-sm { width: 16px; height: 16px; margin-left: 4px; }

/* =========================================
   MODE: SINGLE / FULL PAGE
   ========================================= */
.mode-full .full-entry-layout {
  max-width: 800px;
  margin: 0 auto;
  padding: 0 1rem 4rem;
}
.mode-full .nav-header { padding: 1.5rem 0; margin-bottom: 0rem; }
.mode-full .back-link { display: inline-flex; align-items: center; color: #007aff; text-decoration: none; font-weight: 500; }
.mode-full .headline { font-size: 2.2rem; line-height: 1.1; font-weight: 800; margin-bottom: 0.5rem; }
.mode-full .media-container { margin: 1.5rem 0; border-radius: 8px; overflow: hidden; background: #000; }
.mode-full .description-content { overflow: hidden; transition: max-height 0.3s ease; color: #888;}
.mode-full .description-collapsed { display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; max-height: 6em; }
.mode-full .description { font-size: 1.1rem; line-height: 1.6; white-space: pre-wrap; margin: 0; }
.mode-full .read-more-toggle { background: none; border: none; padding: 0; color: #BA0D61; font-weight: 600; font-size: 0.95rem; margin-top: 0.5rem; cursor: pointer; }
.mode-full .divider { height: 1px; background: #eaeaea; margin: 2rem 0; }
.mode-full .video-wrapper { position: relative; padding-bottom: 56.25%; height: 0; }
.mode-full .video-wrapper iframe { position: absolute; top: 0; left: 0; width: 100%; height: 100%; }
.mode-full .image-wrapper img { width: 100%; display: block; }
.mode-full .meta-top { display: flex; gap: 1rem; font-size: 0.85rem; text-transform: uppercase; font-weight: 600; margin-bottom: 0.5rem; }
.mode-full .date { color: #8e8e93; }
.mode-full .source-btn { 
  display: inline-flex; align-items: center; 
  background: #f2f2f7; color: #007aff; 
  padding: 0.5rem 1rem; border-radius: 20px; 
  text-decoration: none; font-weight: 600; font-size: 0.9rem; 
  transition: background 0.2s;
}
.mode-full .source-btn:hover { background: #e5e5ea; }

.action-bar {
  display: flex;
  justify-content: flex-end; 
  padding: 0rem 0rem 1rem 0rem;
}

/* =========================================
   MODE: TILE / CARD
   ========================================= */
.tile-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  border: 1px solid rgba(0,0,0,0.05);
  transition: transform 0.2s, box-shadow 0.2s;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
}

/* Tile Image */
.tile-media {
  position: relative;
  height: 200px;
  overflow: hidden;
  flex-shrink: 0;
  background: #f0f0f0;
}
.tile-img {
  width: 100%; height: 100%; object-fit: cover;
  transition: transform 0.5s ease;
}
.tile-card:hover .tile-img { transform: scale(1.05); }

/* Tile Content Body */
.tile-content {
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  flex-grow: 1; /* Tile Content grows to fill card height */
}

.tile-meta { font-size: 0.75rem; color: #8e8e93; font-weight: 600; margin-bottom: 0.5rem; display: flex; align-items: center; }
.tile-dot { margin: 0 4px; }
.tile-source { color: #BA0D61; text-transform: uppercase; }

.tile-title {
  font-size: 1.2rem;
  font-weight: 700;
  line-height: 1.25;
  margin: 0 0 0.75rem;
  color: #111;
  display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;
  flex-shrink: 0;
}

.tile-desc {
  font-size: 0.9rem;
  color: #555;
  line-height: 1.4;
  margin-bottom: 1rem;
  color: #888;
  display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden;
  flex-shrink: 0;
  flex-grow: 0;   
}

/* Tile Actions */
.tile-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 0.5rem;
  flex-shrink: 0;
}

.read-story-btn {
  background: #f2f2f7;
  color: #333;
  border: none;
  padding: 0.4rem 0.8rem;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  transition: background 0.2s;
}
.read-story-btn:hover { background: #e5e5ea; }

.tile-source-link {
  display: inline-flex; align-items: center;
  color: #8e8e93; text-decoration: none;
  font-size: 0.8rem; font-weight: 500;
  transition: color 0.2s;
}
.tile-source-link:hover { color: #BA0D61; }
.source-text { margin-right: 4px; }

/* Tile Comments */
.tile-comments {
  background: #fcfcfc;
  margin: 0 -1.25rem -1.25rem -1.25rem;
  padding: 0.75rem 1.25rem;
  border-top: 1px solid #f0f0f0;
  flex-grow: 1; 
  display: flex;
  flex-direction: column;
}
.tile-divider { margin-bottom: 0.5rem; }
.comments-preview-list {
  flex-grow: 1; 
}

/* =========================================
   STYLE VARIANTS
   ========================================= */
.style-hero .tile-media { height: 350px; }
.style-hero .tile-title { font-size: 1.8rem; }
.style-compact .tile-media { height: 150px; }
.style-compact .tile-title { font-size: 1rem; }
.style-tall .tile-media { height: 280px; }
.style-wide .tile-media { height: 240px; }

@media (max-width: 768px) {
  .style-hero .tile-media { height: 220px; }
  .style-hero .tile-title { font-size: 1.4rem; }
}
</style>



// WEBPACK FOOTER //
// src/components/site/Entry.vue